import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  getAppSettings,
  getPublicTeamLiveStatuses,
  getPublicTeams,
} from "@/lib/api";
import { coerceBool } from "@/lib/coerce";
import { deriveLiveStatusPillState } from "@/features/live/components/LiveStatusPill";
import { unscopedKey } from "@/lib/queryClient";
import { useDocumentMeta } from "@/lib/useDocumentMeta";
import { TeamCard } from "../components/TeamCard";

/**
 * `/teams` — public team index (M12a).
 *
 * Anonymous, no auth gate. Reads `/api/teams/public` (cached 60s)
 * and renders each public team as a `TeamCard`. Empty-state copy
 * mirrors the M12 spec: "No public team pages yet — sign in if
 * you're part of a team."
 *
 * Header pulls `season_title` + uploaded `club_logo_url` from
 * `/api/settings` so the page reads as the operator-branded club
 * landing instead of a generic "teams" heading. Falls back to a
 * plain "teams" heading on the (rare) settings-fetch failure.
 */
export function TeamsIndexPage() {
  const teamsQuery = useQuery({
    queryKey: unscopedKey("public-teams"),
    queryFn: getPublicTeams,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });

  const settingsQuery = useQuery({
    queryKey: unscopedKey("app-settings"),
    queryFn: getAppSettings,
    staleTime: 60_000,
  });

  const settingsLiveEnabled = settingsQuery.data
    ? coerceBool(settingsQuery.data.settings.live_enabled)
    : true;
  const teamLiveStatusesQuery = useQuery({
    queryKey: unscopedKey("public-team-statuses"),
    queryFn: getPublicTeamLiveStatuses,
    refetchInterval: settingsLiveEnabled ? 10_000 : false,
    refetchIntervalInBackground: false,
    enabled: settingsLiveEnabled,
  });
  const teamLiveBySlug = useMemo(() => {
    const map = new Map<string, { active: boolean; ready: boolean }>();
    for (const entry of teamLiveStatusesQuery.data?.teams ?? []) {
      map.set(entry.slug, { active: entry.active, ready: entry.ready });
    }
    return map;
  }, [teamLiveStatusesQuery.data]);
  const teamLiveReachable = teamLiveStatusesQuery.data?.reachable;

  const clubTitle = (settingsQuery.data?.settings?.season_title ?? "").trim();
  const clubLogoUrl = settingsQuery.data?.assets?.club_logo_url ?? "";
  const headingTitle = clubTitle || "teams";

  useDocumentMeta({
    title: clubTitle ? `${clubTitle} · tktic` : "Teams · tktic",
    description: clubTitle
      ? `Browse public team pages for ${clubTitle}.`
      : "Browse public team pages on tktic.",
    ogType: "website",
  });

  return (
    <main
      data-testid="teams-index"
      className="min-h-[60vh] bg-canvas text-ink"
    >
      <header className="border-b border-rule px-6 py-8">
        <div className="mx-auto flex max-w-6xl flex-col gap-3">
          <p className="font-mono text-xs uppercase tracking-wider text-ink-muted">
            tktic · teams
          </p>
          <div className="flex items-center gap-4">
            {clubLogoUrl ? (
              <img
                src={clubLogoUrl}
                alt=""
                data-testid="teams-index-club-logo"
                className="h-12 w-12 shrink-0 rounded object-contain sm:h-16 sm:w-16"
              />
            ) : null}
            <h1
              data-testid="teams-index-heading"
              className="font-display text-3xl tracking-display text-ink sm:text-4xl"
            >
              {headingTitle}
            </h1>
          </div>
          <p className="max-w-2xl font-body text-base text-ink-secondary">
            Browse our club teams. Pick one to see matches, replays, and
            live broadcasts.
          </p>
        </div>
      </header>

      <section className="mx-auto max-w-6xl px-6 py-10">
        {teamsQuery.isLoading ? (
          <p
            data-testid="teams-index-loading"
            className="font-mono text-sm text-ink-muted"
          >
            Loading teams…
          </p>
        ) : teamsQuery.isError ? (
          <p
            data-testid="teams-index-error"
            className="font-mono text-sm text-destructive"
          >
            Couldn&rsquo;t load the team list. Please refresh in a moment.
          </p>
        ) : (teamsQuery.data ?? []).length === 0 ? (
          <EmptyState />
        ) : (
          <ul
            data-testid="teams-index-grid"
            role="list"
            className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
          >
            {(teamsQuery.data ?? []).map((team) => (
              <li key={team.id}>
                <TeamCard
                  team={team}
                  liveState={deriveLiveStatusPillState(
                    teamLiveReachable,
                    teamLiveBySlug.get(team.slug),
                  )}
                />
              </li>
            ))}
          </ul>
        )}
      </section>
    </main>
  );
}

function EmptyState() {
  return (
    <div
      data-testid="teams-index-empty"
      className="flex flex-col items-start gap-3 rounded border border-rule bg-surface p-6"
    >
      <h2 className="font-display text-lg tracking-tight text-ink">
        no public team pages yet
      </h2>
      <p className="font-body text-sm text-ink-secondary">
        Sign in if you&rsquo;re part of a team — team admins can opt in to a
        public page from Coach Settings.
      </p>
      <Link
        to="/login"
        data-testid="teams-index-empty-login"
        className="font-display text-sm font-semibold text-ink underline decoration-accent decoration-2 underline-offset-4 hover:decoration-[3px]"
      >
        Sign in
      </Link>
    </div>
  );
}
